/**
 * pathfinding/astar.ts — Finding the Way Home
 *
 * A* pathfinding on the isometric tile grid. Used by creatures
 * (wander, follow, flee) and can also upgrade the player's movement
 * from single-step to real multi-tile paths.
 *
 * Designed for amortization: long paths can be computed over multiple
 * frames by limiting the max nodes explored per call.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TilePos {
  tileX: number;
  tileY: number;
}

/** Walkability checker — abstracts away the specific tile data structure */
export type WalkableCheck = (x: number, y: number) => boolean;

export interface PathOptions {
  /** Maximum path length in tiles (default 20) */
  maxLength?: number;
  /** Route around furniture positions (default true) */
  avoidFurniture?: boolean;
  /** Allow diagonal movement (default true) */
  allowDiagonal?: boolean;
  /** Entity footprint size: 1 = single tile, 2 = 2x2 (default 1) */
  entitySize?: number;
  /** Maximum nodes to explore before yielding (default 200, 0 = unlimited) */
  maxNodes?: number;
}

// ─── A* Implementation ──────────────────────────────────────────────────────

interface AStarNode {
  x: number;
  y: number;
  g: number;     // cost from start
  h: number;     // heuristic to goal
  f: number;     // g + h
  parent: AStarNode | null;
}

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  // Octile distance (accounts for diagonal moves costing sqrt(2))
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy);
}

/**
 * Find a path from `from` to `to` on the tile grid.
 *
 * @param isWalkable  Function that returns true if a tile is walkable
 * @param from        Start tile
 * @param to          Goal tile
 * @param options     Pathfinding options
 * @returns           Array of tiles from start to goal (inclusive), or null if no path
 */
export function findPath(
  isWalkable: WalkableCheck,
  from: TilePos,
  to: TilePos,
  options?: PathOptions
): TilePos[] | null {
  const maxLength = options?.maxLength ?? 20;
  const allowDiag = options?.allowDiagonal ?? true;
  const entitySize = options?.entitySize ?? 1;
  const maxNodes = options?.maxNodes ?? 200;

  // Quick exit: already there
  if (from.tileX === to.tileX && from.tileY === to.tileY) {
    return [{ tileX: from.tileX, tileY: from.tileY }];
  }

  // Quick exit: goal not walkable
  if (!isWalkableForSize(isWalkable, to.tileX, to.tileY, entitySize)) {
    return null;
  }

  // Open set (simple sorted array — sufficient for small grids)
  const open: AStarNode[] = [];
  const closed = new Set<string>();

  const startNode: AStarNode = {
    x: from.tileX,
    y: from.tileY,
    g: 0,
    h: heuristic(from.tileX, from.tileY, to.tileX, to.tileY),
    f: 0,
    parent: null,
  };
  startNode.f = startNode.g + startNode.h;
  open.push(startNode);

  let explored = 0;

  // Neighbor offsets
  const cardinals: [number, number][] = [
    [0, -1], [1, 0], [0, 1], [-1, 0],
  ];
  const diagonals: [number, number][] = [
    [-1, -1], [1, -1], [1, 1], [-1, 1],
  ];

  while (open.length > 0) {
    if (maxNodes > 0 && explored >= maxNodes) {
      // Amortization limit reached — return best partial path
      return null;
    }

    // Pop node with lowest f
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f) bestIdx = i;
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);

    // Goal check
    if (current.x === to.tileX && current.y === to.tileY) {
      return reconstructPath(current, maxLength);
    }

    const key = `${current.x},${current.y}`;
    if (closed.has(key)) continue;
    closed.add(key);
    explored++;

    // Expand neighbors
    const directions = allowDiag
      ? [...cardinals, ...diagonals]
      : cardinals;

    for (const [dx, dy] of directions) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      const nKey = `${nx},${ny}`;

      if (closed.has(nKey)) continue;
      if (!isWalkableForSize(isWalkable, nx, ny, entitySize)) continue;

      // Diagonal: check both adjacent cardinals are walkable (prevent corner cutting)
      if (dx !== 0 && dy !== 0) {
        if (
          !isWalkableForSize(isWalkable, current.x + dx, current.y, entitySize) ||
          !isWalkableForSize(isWalkable, current.x, current.y + dy, entitySize)
        ) {
          continue;
        }
      }

      const moveCost = (dx !== 0 && dy !== 0) ? Math.SQRT2 : 1;
      const g = current.g + moveCost;

      // Check if already in open with better g
      const existing = open.find((n) => n.x === nx && n.y === ny);
      if (existing && existing.g <= g) continue;

      const h = heuristic(nx, ny, to.tileX, to.tileY);
      const node: AStarNode = { x: nx, y: ny, g, h, f: g + h, parent: current };

      if (existing) {
        // Update existing
        existing.g = g;
        existing.f = g + h;
        existing.parent = current;
      } else {
        open.push(node);
      }
    }
  }

  return null; // no path found
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function isWalkableForSize(
  isWalkable: WalkableCheck,
  x: number,
  y: number,
  size: number
): boolean {
  for (let dy = 0; dy < size; dy++) {
    for (let dx = 0; dx < size; dx++) {
      if (!isWalkable(x + dx, y + dy)) return false;
    }
  }
  return true;
}

function reconstructPath(node: AStarNode, maxLength: number): TilePos[] {
  const path: TilePos[] = [];
  let current: AStarNode | null = node;

  while (current) {
    path.push({ tileX: current.x, tileY: current.y });
    current = current.parent;
  }

  path.reverse();

  // Trim to max length
  if (path.length > maxLength) {
    return path.slice(0, maxLength);
  }

  return path;
}

// ─── Distance Utilities ─────────────────────────────────────────────────────

/** Manhattan distance between two tiles */
export function manhattanDist(a: TilePos, b: TilePos): number {
  return Math.abs(a.tileX - b.tileX) + Math.abs(a.tileY - b.tileY);
}

/** Euclidean distance between two tiles */
export function euclideanDist(a: TilePos, b: TilePos): number {
  const dx = a.tileX - b.tileX;
  const dy = a.tileY - b.tileY;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Chebyshev (chessboard) distance */
export function chebyshevDist(a: TilePos, b: TilePos): number {
  return Math.max(Math.abs(a.tileX - b.tileX), Math.abs(a.tileY - b.tileY));
}
